import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { CustomError } from '#lib/custom-error';
import { FriendlyErrorMessage } from '#models/error/error-message.model';
import { logger } from '#lib/logger';
import { SupabaseService } from '#services/supabase/supabase.service';
import { BucketName } from '#models/db/asset/asset.model';

/**
 * How long a signed URL stays valid, in seconds, keyed by bucket.
 *
 * Vault documents are deliberately short-lived. Every vault caller mints a URL and consumes it
 * within a single round trip — `FilePickerService.getFileSignedUrl` hands it straight to a
 * download, and `UploadManagerService` hands it straight to `saveFile()` — so nothing needs it
 * to outlive the request. A signed URL is a bearer token for that object until it expires, and
 * the vault holds wills, powers of attorney and health directives, so the window is kept
 * narrow.
 *
 * The other buckets stay at a day because their URLs are parked on models and re-rendered
 * long after they are minted — see `MemoryAsset.thumbnail_signed_url` and `storage_signed_url`,
 * which back the asset grid.
 *
 * Typed against `BucketName` so a new bucket has to declare its own lifetime rather than
 * silently inheriting one.
 */
const SIGNED_URL_TTL_SECONDS: Record<BucketName, number> = {
  'memory-asset-uploads': 86400,
  'memory-thumbnail-uploads': 86400,
  'profile-picture-uploads': 86400,
  'vault-asset-uploads': 60,
};

@Injectable({
  providedIn: 'root',
})
export class AssetRepositoryService {
  progressPercentage$ = new BehaviorSubject<string | null>(null);

  constructor(
    private supabaseClient: SupabaseService,
    private injector: Injector
  ) {}

  /**
   * @deprecated Use `UploadManagerService.processQueue()` instead.
   * Kept for backward compatibility with FilePickerService and other legacy callers.
   */
  async processAllQueuedMedia(bucketName?: BucketName): Promise<void> {
    const { UploadManagerService } = await import(
      '#services/upload-manager/upload-manager.service'
    );
    const uploadManager = this.injector.get(UploadManagerService);

    await uploadManager.clearStaleItems();
    await uploadManager.processQueue(bucketName);
  }

  /**
   * Generates a signed URL for accessing a file in the specified storage bucket.
   *
   * @param bucketName - The name of the storage bucket. Also selects how long the URL stays
   * valid — see {@link SIGNED_URL_TTL_SECONDS}.
   * @param path - The path to the file within the bucket.
   * @returns A promise that resolves to the signed URL string.
   * @throws Throws an error if the signed URL cannot be generated or if an unknown error occurs.
   */
  async getSignedUrl(bucketName: BucketName, path: string): Promise<string> {
    const { data, error } = await this.supabaseClient.supabase.storage
      .from(bucketName)
      .createSignedUrl(path, SIGNED_URL_TTL_SECONDS[bucketName]);

    if (error) {
      logger.log(error, 'AssetRepositoryService.getSignedUrl -> error');

      throw error;
    }

    if (!data?.signedUrl) {
      throw new CustomError(FriendlyErrorMessage.UnknownError);
    }

    return data.signedUrl;
  }
}
